import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import IOffersWonContext from './interfaces/IOffersWonContext';
import RoundmanagerApi from '../../../common/services/roundmanager-api';
import IPagination from '../../../common/interfaces/IPagination';
import IWonOfferResponse from '../../../common/interfaces/offers/IWonOfferResponse';
import IWonFiltersModel from '../../../common/interfaces/IFiltersModel';
import { useToast } from '../toast/toast.context';

const INITIAL_PAGE = 1;

export const OffersWonContext = React.createContext<IOffersWonContext>(
    {} as IOffersWonContext,
);

export function OffersWonProvider({ children }: Readonly<PropsWithChildren>) {
    const { t } = useTranslation();
    const { setToast } = useToast();

    const [wonOffersList, setWonOffersList] = useState<IWonOfferResponse[]>(
        [] as IWonOfferResponse[],
    );

    const page = useRef(0);
    const hasMore = useRef(true);

    const handleFetchResponse = useCallback(
        (response: IPagination<IWonOfferResponse>) => {
            if (page.current === INITIAL_PAGE) {
                setWonOffersList(response.data);
            } else {
                setWonOffersList(state => [...state, ...response.data]);
            }

            hasMore.current = page.current < response.totalPages;
        },
        [],
    );

    const handleFetchError = useCallback(
        (error: Error) => {
            setToast('negative', t('Error'), error.message);
        },
        [setToast, t],
    );

    const getWonOffersPaginated = useCallback(
        async (filters: IWonFiltersModel) => {
            const { response, error } =
                await RoundmanagerApi.Offers.GetWonOffersPaginated(
                    filters,
                    page.current,
                );

            if (response !== undefined) {
                handleFetchResponse(response);
            } else {
                handleFetchError(error!);
            }
        },
        [handleFetchResponse, handleFetchError],
    );

    const fetchFirstPage = useCallback(
        async (filters: IWonFiltersModel) => {
            page.current = INITIAL_PAGE;
            await getWonOffersPaginated(filters);
        },
        [getWonOffersPaginated],
    );

    const fetchNextPage = useCallback(
        async (filters: IWonFiltersModel) => {
            page.current++;

            if (page.current === INITIAL_PAGE || hasMore.current) {
                await getWonOffersPaginated(filters);
            }
        },
        [getWonOffersPaginated],
    );

    const updateWonOffer = useCallback(
        (wonOffer: IWonOfferResponse) => {
            setWonOffersList(prevItems =>
                prevItems.map((item: IWonOfferResponse) =>
                    item.offerId === wonOffer.offerId
                        ? {
                              ...item,
                              offerDate: wonOffer.offerDate,
                              country: wonOffer.country,
                              load: wonOffer.load,
                              status: wonOffer.status,
                              loadStatus: wonOffer.loadStatus,
                              cancellationReason: wonOffer.cancellationReason,
                              cancellationDate: wonOffer.cancellationDate,
                              closeReason: wonOffer.closeReason,
                          }
                        : item,
                ),
            );
        },
        [setWonOffersList],
    );

    const provided = useMemo(
        () =>
            ({
                wonOffersList,
                updateWonOffer,
                fetchFirstPage,
                fetchNextPage,
                hasMore,
            }) as IOffersWonContext,
        [fetchFirstPage, fetchNextPage, updateWonOffer, wonOffersList],
    );

    useEffect(() => {
        if (page.current === 0) {
            fetchFirstPage({} as IWonFiltersModel);
        }
    }, [fetchFirstPage]);

    return (
        <OffersWonContext.Provider value={provided}>
            {children}
        </OffersWonContext.Provider>
    );
}

export const useOffersWon = (): IOffersWonContext =>
    useContext(OffersWonContext);
